import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Button,
  Chip,
  Avatar,
  Tabs,
  Tab,
  Paper,
  Divider,
  LinearProgress
} from '@mui/material';
import Grid from '@mui/material/Grid'; 
import {
  School,
  Security,
  People,
  TrendingUp,
  ExitToApp as LogoutIcon,
  Dashboard as DashboardIcon,
  TableChart as TableIcon,
  ArrowForward,
  AccessTime,
  EventNote,
  Warning,
  CheckCircle,
  Assessment,
  TrendingDown,
  BarChart as BarChartIcon,
  Error as ErrorIcon,
  Business
} from '@mui/icons-material';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';

import { TrainingRecord, DashboardStats } from '../types';
import { ApiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import TrainingDataTable from './TrainingDataTable';

const GridAny = Grid as any;
const COLORS = ['#344767', '#1a73e8', '#667eea', '#4caf50', '#fb8c00', '#e91e63'];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const StatCard = ({ title, value, icon, color }: any) => (
  <Card sx={{ 
    height: '100%', 
    position: 'relative', 
    overflow: 'visible', 
    borderRadius: 3, 
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'transform 0.3s, box-shadow 0.3s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 30px rgba(0,0,0,0.12)'
    }
  }}>
    <Box sx={{ 
      position: 'absolute', 
      top: -20, 
      left: 20, 
      width: 60, 
      height: 60, 
      borderRadius: 3, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: color,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      color: 'white'
    }}>
      {icon}
    </Box>
    <CardContent sx={{ pt: 5, textAlign: 'right' }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5, fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#344767' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const Dashboard: React.FC = () => {
  const { user, userRole, logout } = useAuth();
  const [data, setData] = useState<TrainingRecord[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  const loadDashboardData = useCallback(async () => {
    if (!user?.email) return;

    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getTrainingData(user.email);
      
      if (response.status === 'success') {
        // 빈 행 필터링: RecordID와 성명이 있는 데이터만 포함
        const filteredData = (response.data || []).filter(
          (record: TrainingRecord) => record.RecordID && record.RecordID.trim() !== '' && record.성명 && record.성명.trim() !== ''
        );
        setData(filteredData);
        
        // stats도 필터링된 데이터 기준으로 재계산
        if (response.stats) {
          const recalculatedStats = {
            ...response.stats,
            totalRecords: filteredData.length,
            initialTraining: filteredData.filter((r: TrainingRecord) => r.초기정기구분 === '초기').length,
            regularTraining: filteredData.filter((r: TrainingRecord) => r.초기정기구분 === '정기').length,
            recentRecords: filteredData.slice(0, 5)
          };
          setStats(recalculatedStats);
        }
      } else {
        setError(response.message || '데이터 로드 실패');
      }
    } catch (error: any) {
      setError('데이터 로드 실패: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const advancedStats = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        expiringCount: 0,
        expiredCount: 0,
        complianceByTeam: {},
        monthlyTypeData: [],
        currentYearCount: 0,
        yearOverYear: null,
        statusDistribution: {},
        departmentTypeDistribution: [],
        teamTotalCounts: {}
      };
    }

    const today = new Date();
    const thirtyDaysLater = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
    const currentYear = today.getFullYear();
    const lastYear = currentYear - 1;

    // 만료 임박자 (30일 이내) - 395일 기준
    const expiringRecords = data.filter(record => {
      if (!record.수료일자) return false;
      const completionDate = new Date(record.수료일자);
      const expiryDate = new Date(completionDate.getTime() + 395 * 24 * 60 * 60 * 1000);
      return expiryDate >= today && expiryDate <= thirtyDaysLater;
    });

    // 이미 만료된 사람 (395일 초과)
    const expiredRecords = data.filter(record => {
      if (!record.수료일자) return false;
      const completionDate = new Date(record.수료일자);
      const expiryDate = new Date(completionDate.getTime() + 395 * 24 * 60 * 60 * 1000);
      return expiryDate < today;
    });

    const teamCounts: { [key: string]: { total: number; compliant: number } } = {};
    data.forEach(record => {
      const team = record.소속팀지점 || '미분류';
      if (!teamCounts[team]) teamCounts[team] = { total: 0, compliant: 0 };
      teamCounts[team].total++;
      if (record.처리상태 === '완료' || record.처리상태 === '자동처리') {
        teamCounts[team].compliant++;
      }
    });

    const complianceByTeam: { [key: string]: number } = {};
    Object.entries(teamCounts).forEach(([team, counts]) => {
      complianceByTeam[team] = Math.round((counts.compliant / counts.total) * 100);
    });

    const departmentTypeCounts: { 
      [key: string]: { 초기: number; 정기: number; total: number } 
    } = {};
    
    data.forEach(record => {
      const team = record.소속팀지점 || '미분류';
      if (!departmentTypeCounts[team]) {
        departmentTypeCounts[team] = { 초기: 0, 정기: 0, total: 0 };
      }
      departmentTypeCounts[team].total++;
      if (record.초기정기구분 === '초기') departmentTypeCounts[team].초기++;
      else if (record.초기정기구분 === '정기') departmentTypeCounts[team].정기++;
    });

    const departmentTypeDistribution = Object.entries(departmentTypeCounts)
      .sort(([,a], [,b]) => b.total - a.total)
      .slice(0, 6)
      .map(([team, counts]) => ({
        name: team,
        초기: counts.초기,
        정기: counts.정기,
        초기비율: Math.round((counts.초기 / counts.total) * 100),
        정기비율: Math.round((counts.정기 / counts.total) * 100)
      }));

    const monthlyTypeCounts: { [key: string]: { 초기: number; 정기: number; month: string } } = {};
    data.forEach(record => {
      if (!record.수료일자) return;
      const month = record.수료일자.substring(0, 7);
      if (!monthlyTypeCounts[month]) {
        monthlyTypeCounts[month] = { 초기: 0, 정기: 0, month };
      }
      if (record.초기정기구분 === '초기') monthlyTypeCounts[month].초기++;
      else if (record.초기정기구분 === '정기') monthlyTypeCounts[month].정기++;
    });

    const monthlyTypeData = Object.entries(monthlyTypeCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-12)
      .map(([month, counts]) => {
        const [year, m] = month.split('-');
        return {
          name: `${parseInt(m)}월`,
          fullName: `${year}년 ${parseInt(m)}월`,
          초기: counts.초기,
          정기: counts.정기,
          total: counts.초기 + counts.정기
        };
      });

    const currentYearCount = data.filter(record => 
      record.수료일자?.startsWith(String(currentYear))
    ).length;
    const lastYearCount = data.filter(record => 
      record.수료일자?.startsWith(String(lastYear))
    ).length;
    const yearOverYear = lastYearCount === 0 ? null : 
      Math.round(((currentYearCount - lastYearCount) / lastYearCount) * 100);

    const statusDistribution: { [key: string]: number } = {};
    data.forEach(record => {
      const status = record.처리상태 || '기타';
      statusDistribution[status] = (statusDistribution[status] || 0) + 1;
    });

    // 부서별 총 이수자 수 (초기+정기)
    const teamTotalCounts: { [key: string]: number } = {};
    data.forEach(record => {
      const team = record.소속팀지점 || '미분류';
      teamTotalCounts[team] = (teamTotalCounts[team] || 0) + 1;
    });

    return {
      expiringCount: expiringRecords.length,
      expiredCount: expiredRecords.length,
      complianceByTeam,
      monthlyTypeData,
      currentYearCount,
      yearOverYear,
      statusDistribution,
      departmentTypeDistribution,
      teamTotalCounts
    };
  }, [data]);

  const getRoleDisplayName = (role: string | null) => {
    switch (role) {
      case 'developer': return '개발자';
      case 'admin': return '관리자';
      case 'viewer': return '열람자';
      default: return '사용자';
    }
  };

  const getRoleColor = (role: string | null): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (role) {
      case 'developer': return 'error';
      case 'admin': return 'warning';
      case 'viewer': return 'info';
      default: return 'default';
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100vh" bgcolor="#f8f9fa">
        <CircularProgress size={60} thickness={4} sx={{ color: '#344767' }} />
        <Typography sx={{ mt: 2, color: '#666', fontWeight: 500 }}>데이터를 불러오는 중입니다...</Typography>
      </Box>
    );
  }

  const teamChartData = stats ? Object.entries(stats.byTeam)
    .sort(([,a], [,b]) => b - a)
    .map(([team, count]) => ({
      name: team || '미분류',
      value: count
    })) : [];

  const complianceData = Object.entries(advancedStats.complianceByTeam)
    .sort(([,a], [,b]) => a - b)
    .map(([team, rate]) => ({
      name: team,
      rate: rate
    }));

  return (
    <Box sx={{ bgcolor: '#f0f2f5', minHeight: '100vh' }}>
      <AppBar 
        position="sticky" 
        elevation={0} 
        sx={{ 
          bgcolor: 'rgba(255,255,255,0.85)', 
          backdropFilter: 'blur(20px)', 
          borderBottom: '1px solid rgba(0,0,0,0.08)' 
        }}
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: '#344767', fontWeight: 700 }}>
            ✈️ 에어제타 <Typography component="span" variant="h6" sx={{ fontWeight: 400 }}>항공보안교육훈련</Typography>
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip 
              label={getRoleDisplayName(userRole)} 
              color={getRoleColor(userRole)}
              size="small"
              sx={{ fontWeight: 600 }} 
            />
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ color: '#344767', fontWeight: 600 }}>
                {user?.displayName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#7b809a' }}>
                {user?.email}
              </Typography>
            </Box>
            <Avatar src={user?.photoURL || undefined} sx={{ width: 36, height: 36, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }} />
            <Button 
              onClick={logout}
              startIcon={<LogoutIcon />}
              sx={{ color: '#7b809a', minWidth: 'auto', textTransform: 'none' }}
            >
              로그아웃
            </Button>
          </Box>
        </Toolbar>
        
        <Container maxWidth="xl">
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 50, fontSize: '0.95rem' },
              '& .Mui-selected': { color: '#344767' },
              '& .MuiTabs-indicator': { backgroundColor: '#344767', height: 3 }
            }}
          >
            <Tab icon={<DashboardIcon sx={{ mb: 0, mr: 1 }} />} label="대시보드" iconPosition="start" />
            <Tab icon={<TableIcon sx={{ mb: 0, mr: 1 }} />} label="데이터 관리" iconPosition="start" />
          </Tabs>
        </Container>
      </AppBar>

      {error && (
        <Container maxWidth="xl" sx={{ pt: 3 }}>
          <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 2 }}>
            {error}
          </Alert>
        </Container>
      )}

      <TabPanel value={tabValue} index={0}>
        <Container maxWidth={false} sx={{ px: { xs: 2, md: 4 } }}>
          
          {/* 만료자 경고 (빨간색) */}
          {advancedStats.expiredCount > 0 && (
            <Alert 
              severity="error" 
              icon={<ErrorIcon fontSize="large" />}
              sx={{ 
                mb: 2, 
                mt: 3,
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(211,47,47,0.2)',
                border: '2px solid #d32f2f'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                🚨 자격만료 경고 - 즉시 조치 필요
              </Typography>
              <Typography variant="body2">
                <strong>{advancedStats.expiredCount}명</strong>의 교육 자격이 이미 만료되었습니다. 재교육이 즉시 필요합니다!
              </Typography>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => setTabValue(1)}
                sx={{ mt: 1, color: '#d32f2f', borderColor: '#d32f2f' }}
              >
                만료자 확인하기
              </Button>
            </Alert>
          )}

          {/* 만료 임박 경고 (주황색) */}
          {advancedStats.expiringCount > 0 && (
            <Alert 
              severity="warning" 
              icon={<Warning fontSize="large" />}
              sx={{ 
                mb: 4, 
                mt: advancedStats.expiredCount > 0 ? 0 : 3,
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(251,140,0,0.2)',
                border: '2px solid #fb8c00'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                ⚠️ 자격만료 임박 경고
              </Typography>
              <Typography variant="body2">
                <strong>{advancedStats.expiringCount}명</strong>의 교육 이수자가 30일 이내에 자격이 만료됩니다.
              </Typography>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => setTabValue(1)}
                sx={{ mt: 1, color: '#fb8c00', borderColor: '#fb8c00' }}
              >
                대상자 확인하기
              </Button>
            </Alert>
          )}

          {/* 상단 통계 카드 - 5개 */}
          <Box sx={{ mb: 5, mt: 4 }}>
            <GridAny container spacing={3}>
              <GridAny item xs={12} sm={6} md={2.4}>
                <StatCard 
                  title="총 이수자" 
                  value={stats?.totalRecords || 0} 
                  icon={<People fontSize="large" />}
                  color="linear-gradient(135deg, #344767 0%, #212529 100%)"
                />
              </GridAny>
              <GridAny item xs={12} sm={6} md={2.4}>
                <StatCard 
                  title="초기 교육" 
                  value={stats?.initialTraining || 0} 
                  icon={<School fontSize="large" />}
                  color="linear-gradient(135deg, #e91e63 0%, #b0003a 100%)"
                />
              </GridAny>
              <GridAny item xs={12} sm={6} md={2.4}>
                <StatCard 
                  title="정기 교육" 
                  value={stats?.regularTraining || 0} 
                  icon={<TrendingUp fontSize="large" />}
                  color="linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)"
                />
              </GridAny>
              <GridAny item xs={12} sm={6} md={2.4}>
                <StatCard 
                  title="만료 임박 (30일)" 
                  value={advancedStats.expiringCount} 
                  icon={<Warning fontSize="large" />}
                  color="linear-gradient(135deg, #fb8c00 0%, #e65100 100%)"
                />
              </GridAny>
              <GridAny item xs={12} sm={6} md={2.4}>
                <StatCard 
                  title="만료자" 
                  value={advancedStats.expiredCount} 
                  icon={<ErrorIcon fontSize="large" />}
                  color="linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)"
                />
              </GridAny>
            </GridAny>
          </Box>

          {/* 부서별 교육 이수자 카드 */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                bgcolor: '#e8f5e9', 
                color: '#4caf50', 
                p: 1, 
                borderRadius: 2,
                display: 'flex'
              }}>
                <Business />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#344767' }}>
                부서별 교육 이수자 현황
              </Typography>
            </Box>
            <GridAny container spacing={2}>
              {Object.entries(advancedStats.teamTotalCounts)
                .sort(([,a], [,b]) => (b as number) - (a as number))
                .map(([team, count], index) => (
                  <GridAny item xs={6} sm={4} md={2} key={team}>
                    <Paper sx={{ 
                      p: 2, 
                      borderRadius: 2, 
                      textAlign: 'center',
                      border: '1px solid #e9ecef',
                      transition: 'all 0.2s',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transform: 'translateY(-2px)'
                      }
                    }}>
                      <Typography variant="body2" sx={{ color: '#7b809a', mb: 0.5, fontSize: '0.8rem' }}>
                        {team}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS[index % COLORS.length] }}>
                        {count as number}명
                      </Typography>
                    </Paper>
                  </GridAny>
                ))
              }
            </GridAny>
          </Box>

          <Box sx={{ 
            mb: 3, 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#344767', mb: 0.5 }}>
                📊 교육 이수 분석 대시보드
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Training Completion Analytics - 실시간 데이터 기반 현황 분석
              </Typography>
            </Box>
            <Chip 
              icon={<AccessTime sx={{ fontSize: '1rem !important' }} />}
              label={`${new Date().toLocaleDateString('ko-KR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })} ${new Date().toLocaleTimeString('ko-KR', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}`}
              size="medium"
              sx={{ 
                bgcolor: 'white', 
                color: '#546e7a', 
                fontWeight: 600,
                fontFamily: 'monospace',
                border: '1px solid #cfd8dc',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                px: 1.5,
                height: 32
              }}
            />
          </Box>

          <Paper sx={{ 
            p: 4, 
            mb: 4,
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            border: '1px solid #e9ecef'
          }}>
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ 
                  bgcolor: '#e8f5e9', 
                  color: '#4caf50', 
                  p: 1, 
                  borderRadius: 2,
                  display: 'flex'
                }}>
                  <Assessment />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#344767' }}>
                  교육 유형별 월별 추이
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                초기교육 vs 정기교육 이수 현황 비교 (최근 12개월)
              </Typography>
            </Box>
            
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={advancedStats.monthlyTypeData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f2f5" />
                <XAxis 
                  dataKey="name"
                  tickLine={false} 
                  axisLine={false} 
                  tick={{ fill: '#7b809a', fontSize: 13, fontWeight: 600 }} 
                  dy={10} 
                />
                <YAxis 
                  tickLine={false} 
                  axisLine={false} 
                  tick={{ fill: '#7b809a', fontSize: 12 }}
                  label={{ 
                    value: '이수 인원', 
                    angle: -90, 
                    position: 'insideLeft',
                    style: { fill: '#7b809a', fontWeight: 600, fontSize: 12 }
                  }}
                />
                <Tooltip 
                  cursor={{ fill: 'rgba(102,126,234,0.05)' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Paper sx={{ p: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#344767', mb: 1.5 }}>
                            📅 {data.fullName}
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e91e63' }} />
                              <Typography variant="body2">
                                초기교육: <strong>{data.초기}명</strong>
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1a73e8' }} />
                              <Typography variant="body2">
                                정기교육: <strong>{data.정기}명</strong>
                              </Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#344767' }}>
                              총 이수: <strong>{data.total}명</strong>
                            </Typography>
                          </Box>
                        </Paper>
                      );
                    }
                    return null;
                  }}
                />
                <Legend 
                  verticalAlign="top" 
                  height={36}
                  iconType="circle"
                  formatter={(value) => (
                    <span style={{ color: '#546e7a', fontSize: '0.85rem', fontWeight: 600 }}>
                      {value === '초기' ? '초기교육' : '정기교육'}
                    </span>
                  )}
                />
                <Bar dataKey="초기" name="초기" stackId="a" fill="#e91e63" radius={[0, 0, 0, 0]} barSize={32} />
                <Bar dataKey="정기" name="정기" stackId="a" fill="#1a73e8" radius={[6, 6, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* 🔥 고정 픽셀 기반 레이아웃 - 700px + flex + 400px */}
          <Box sx={{ 
            display: 'flex', 
            gap: 4, 
            mb: 4,
            flexWrap: 'wrap'
          }}>
            {/* 1. 부서별 분포 - 700px 고정 (충분한 파이차트 공간) */}
            <Box sx={{ 
              width: { xs: '100%', lg: 700 }, 
              minWidth: { xs: '100%', lg: 700 }
            }}>
              <Paper sx={{ 
                p: 4, 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                height: '100%',
                minHeight: 550,
                border: '1px solid #e9ecef',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ 
                      bgcolor: '#fce4ec', 
                      color: '#e91e63', 
                      p: 1, 
                      borderRadius: 2,
                      display: 'flex'
                    }}>
                      <People />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#344767' }}>
                      부서별 분포
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    팀/지점별 보안교육 이수 비율 분석
                  </Typography>
                </Box>
                
                <Box sx={{ flex: 1, minHeight: 420, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box sx={{ width: '100%', height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
  data={teamChartData}
  cx="50%"
  cy="45%"
  innerRadius={90}
  outerRadius={150}
  paddingAngle={3}
  dataKey="value"
  label={({ name, percent = 0 }) => `${name} ${(percent * 100).toFixed(0)}%`}
  labelLine={{ 
    stroke: '#7b809a', 
    strokeWidth: 1.5
  }}
>

                          {teamChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="white" strokeWidth={3} />
                          ))}
                        </Pie>
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              const percentage = ((data.value / (stats?.totalRecords || 1)) * 100).toFixed(1);
                              return (
                                <Paper sx={{ p: 2.5, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#344767', mb: 1 }}>
                                    🏢 {data.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography variant="body2" color="textSecondary">
                                      👥 총 이수자: <strong style={{ color: '#344767' }}>{data.value}명</strong>
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      📊 전체 비율: <strong style={{ color: '#1a73e8' }}>{percentage}%</strong>
                                    </Typography>
                                  </Box>
                                </Paper>
                              );
                            }
                            return null;
                          }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={80}
                          iconType="circle"
                          wrapperStyle={{ 
                            paddingTop: '30px',
                            fontSize: '14px'
                          }}
                          formatter={(value, entry) => (
                            <span style={{ 
                              color: '#546e7a', 
                              fontSize: '0.9rem', 
                              fontWeight: 600 
                            }}>
                              {value} ({entry.payload?.value}명)
                            </span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Paper>
            </Box>

            {/* 2. 부서별 규정 준수율 - flex: 1 (남은 공간 자동) */}
            <Box sx={{ flex: 1, minWidth: { xs: '100%', lg: 300 } }}>
              <Paper sx={{ 
                p: 4, 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                height: '100%',
                minHeight: 550,
                border: '1px solid #e9ecef',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ 
                      bgcolor: '#e3f2fd', 
                      color: '#1a73e8', 
                      p: 1, 
                      borderRadius: 2,
                      display: 'flex'
                    }}>
                      <CheckCircle />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#344767' }}>
                      부서별 규정 준수율
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Compliance Rate - 항공보안법 기준
                  </Typography>
                </Box>
                
                {complianceData.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {complianceData.map((item, index) => (
                      <Box key={item.name} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#344767' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            fontWeight: 700, 
                            color: item.rate >= 80 ? '#4caf50' : item.rate >= 60 ? '#fb8c00' : '#e91e63' 
                          }}>
                            {item.rate}%
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={item.rate} 
                          sx={{ 
                            height: 12, 
                            borderRadius: 6,
                            bgcolor: '#f0f2f5',
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 6,
                              bgcolor: item.rate >= 80 ? '#4caf50' : item.rate >= 60 ? '#fb8c00' : '#e91e63'
                            }
                          }}
                        />
                      </Box>
                    ))}
                    <Divider sx={{ my: 3 }} />
                    <Box sx={{ 
                      bgcolor: '#f8f9fa', 
                      p: 2, 
                      borderRadius: 2,
                      border: '1px solid #e9ecef'
                    }}>
                      <Typography variant="caption" sx={{ color: '#7b809a', display: 'block', mb: 1 }}>
                        📌 준수율 기준
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#4caf50' }} />
                          <Typography variant="caption" sx={{ color: '#546e7a' }}>
                            80% 이상: 우수
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#fb8c00' }} />
                          <Typography variant="caption" sx={{ color: '#546e7a' }}>
                            60-79%: 주의
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e91e63' }} />
                          <Typography variant="caption" sx={{ color: '#546e7a' }}>
                            60% 미만: 개선필요
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ py: 6, textAlign: 'center', color: '#7b809a' }}>
                    <Typography>데이터가 충분하지 않습니다.</Typography>
                  </Box>
                )}
              </Paper>
            </Box>

            {/* 🔥 3. 부서별 교육 유형 - 400px 고정 (컴팩트) + 실무 가이드 제거 */}
            <Box sx={{ 
              width: { xs: '100%', lg: 400 }, 
              minWidth: { xs: '100%', lg: 400 }
            }}>
              <Paper sx={{ 
                p: 4, 
                borderRadius: 3, 
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
                height: '100%',
                minHeight: 550,
                border: '1px solid #e9ecef',
                transition: 'all 0.3s',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)'
                }
              }}>
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Box sx={{ 
                      bgcolor: '#fff3e0', 
                      color: '#fb8c00', 
                      p: 1, 
                      borderRadius: 2,
                      display: 'flex'
                    }}>
                      <BarChartIcon />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#344767' }}>
                      부서별 교육 유형
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    초기 vs 정기 교육 분포
                  </Typography>
                </Box>
                
                {advancedStats.departmentTypeDistribution.length > 0 ? (
                  <Box sx={{ height: 470 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={advancedStats.departmentTypeDistribution} 
                        layout="vertical"
                        margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f2f5" />
                        <XAxis type="number" hide />
                        <YAxis 
                          dataKey="name" 
                          type="category" 
                          width={80}
                          tick={{ fill: '#7b809a', fontSize: 11 }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <Paper sx={{ p: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', borderRadius: 2 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#344767', mb: 1 }}>
                                    🏢 {data.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#e91e63' }} />
                                      <Typography variant="body2">
                                        초기: <strong>{data.초기}명</strong> ({data.초기비율}%)
                                      </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#1a73e8' }} />
                                      <Typography variant="body2">
                                        정기: <strong>{data.정기}명</strong> ({data.정기비율}%)
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Paper>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar dataKey="초기" stackId="a" fill="#e91e63" />
                        <Bar dataKey="정기" stackId="a" fill="#1a73e8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box sx={{ py: 6, textAlign: 'center', color: '#7b809a' }}>
                    <Typography>데이터가 충분하지 않습니다.</Typography>
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>

          <Paper sx={{ 
            p: 4, 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)', 
            border: '1px solid #e9ecef' 
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#344767' }}>
                  🕒 최근 이수 내역
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  실시간 업데이트되는 최근 5건의 이수 기록
                </Typography>
              </Box>
              <Button 
                endIcon={<ArrowForward />} 
                onClick={() => setTabValue(1)}
                sx={{ 
                  textTransform: 'none', 
                  color: '#344767', 
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: 'rgba(52,71,103,0.1)'
                  }
                }}
              >
                전체 데이터 관리
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            
            {stats && stats.recentRecords && stats.recentRecords.length > 0 ? (
              <Box>
                {stats.recentRecords.slice(0, 5).map((record, index) => (
                  <Box 
                    key={record.RecordID || index}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      p: 2.5,
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: 'white',
                      border: '1px solid #f0f2f5',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                      transition: 'all 0.2s',
                      '&:hover': { 
                        bgcolor: '#f8f9fa', 
                        transform: 'translateX(4px)',
                        borderColor: '#1a73e8'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ 
                        bgcolor: COLORS[index % COLORS.length], 
                        color: 'white', 
                        mr: 3, 
                        fontWeight: 700,
                        width: 44,
                        height: 44,
                        fontSize: '1rem'
                      }}>
                        {record.성명.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#344767' }}>
                          {record.성명}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <Chip 
                            label={record.초기정기구분} 
                            size="small" 
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem', 
                              bgcolor: record.초기정기구분 === '초기' ? '#fce4ec' : '#e3f2fd',
                              color: record.초기정기구분 === '초기' ? '#e91e63' : '#1a73e8',
                              fontWeight: 700
                            }} 
                          />
                          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                            {record.수료기관}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#344767' }}>
                        {record.수료일자}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" sx={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.75rem'
                      }}>
                        {record.수료번호}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 6, textAlign: 'center', color: '#7b809a', bgcolor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="body1">등록된 이수 내역이 없습니다.</Typography>
              </Box>
            )}
          </Paper>
        </Container>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <TrainingDataTable 
          data={data} 
          onRefresh={loadDashboardData}
          userRole={userRole}
        />
      </TabPanel>
    </Box>
  );
};

export default Dashboard;
